import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface WatiConfig {
  apiEndpoint: string;
  accessToken: string;
  tenantId: string;
  isActive: boolean;
}

export interface WatiTemplate {
  id: string;
  provider_template_id: string;
  element_name: string;
  category: string;
  sub_category?: string;
  status: 'APPROVED' | 'PENDING' | 'REJECTED';
  language: string;
  channel: string;
  header?: string;
  body: string;
  footer?: string;
  buttons?: any[];
  parameters?: string[];
  metadata?: any;
  parameter_mapping?: Record<string, string> | null;
  last_synced_at?: Date;
}

export interface WatiTrigger {
  id?: number;
  trigger_name: string;
  trigger_event: 'new_lead' | 'status_change' | 'custom';
  template_id: number | string;
  trigger_conditions?: any;
  parameter_mapping?: Record<string, string>;
  channel: string;
  is_active: boolean;
  priority: number;
  template?: WatiTemplate;
}

export interface MessageLog {
  id: number;
  recipient: string;
  channel: string;
  status: 'pending' | 'processing' | 'sent' | 'failed' | 'delivered';
  sent_at?: Date;
  delivered_at?: Date;
  created_at: Date;
  template?: {
    element_name: string;
    channel: string;
  };
  logs?: Array<{
    provider_response?: any;
    error_message?: string;
    status_code?: number;
  }>;
}

class WatiService {
  private getAuthHeaders() {
    // Get auth token following the same pattern as other services
    let token = Cookies.get('crm_auth_token');

    // Fallback to environment variable if cookie token not found
    if (!token && import.meta.env.VITE_BEARER_TOKEN) {
      token = import.meta.env.VITE_BEARER_TOKEN;
      console.log("WATI Service: Using fallback token from VITE_BEARER_TOKEN");
    }

    if (!token) {
      console.warn("WATI Service: No authentication token found in cookies or environment");
    } else {
      console.log("WATI Service: Using token from", token.startsWith('ey') ? 'JWT token found' : 'simple token found');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Settings Management
  async getSettings(): Promise<WatiConfig | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/integrations/wati/settings`, {
        headers: this.getAuthHeaders()
      });

      if (response.data.success) {
        const settings = response.data.data;
        // Use credentials directly (already parsed by backend)
        if (settings.credentials) {
          const creds = settings.credentials;
          return {
            apiEndpoint: creds.apiEndpoint || '',
            accessToken: creds.accessToken || '',
            tenantId: creds.tenantId || '',
            isActive: settings.is_active || false
          };
        }
        return {
          apiEndpoint: '',
          accessToken: '',
          tenantId: '',
          isActive: settings.is_active || false
        };
      }
      return null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No settings found, return empty config
        return {
          apiEndpoint: '',
          accessToken: '',
          tenantId: '',
          isActive: false
        };
      }
      console.error('Error fetching WATI settings:', error);
      throw error;
    }
  }

  async saveSettings(config: WatiConfig): Promise<void> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/wati/settings`,
        {
          credentials: {
            apiEndpoint: config.apiEndpoint,
            accessToken: config.accessToken,
            tenantId: config.tenantId
          },
          config: {},
          is_active: config.isActive
        },
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving WATI settings:', error);
      throw error;
    }
  }

  async testConnection(): Promise<{ success: boolean; message: string; data?: { templateCount?: number } }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/wati/test`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Connection test failed'
      };
    }
  }

  // Template Management
  async getTemplates(): Promise<WatiTemplate[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/integrations/wati/templates`, {
        headers: this.getAuthHeaders()
      });

      if (response.data.success) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  }

  async syncTemplates(): Promise<{ success: boolean; message: string; data?: WatiTemplate[] }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/wati/sync-templates`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to sync templates'
      };
    }
  }

  async sendTestMessage(templateId: string, recipient: string, parameters: Record<string, string>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/wati/test-message`,
        {
          template_id: templateId,
          recipient,
          parameters
        },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send test message'
      };
    }
  }

  // Trigger Management
  async getTriggers(): Promise<WatiTrigger[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/integrations/wati/triggers`, {
        headers: this.getAuthHeaders()
      });

      if (response.data.success) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching triggers:', error);
      return [];
    }
  }

  async saveTrigger(trigger: WatiTrigger): Promise<{ success: boolean; message: string; data?: WatiTrigger }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/wati/triggers`,
        trigger,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to save trigger'
      };
    }
  }

  async deleteTrigger(triggerId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/integrations/wati/triggers/${triggerId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete trigger'
      };
    }
  }

  async toggleTriggerStatus(triggerId: number): Promise<{ success: boolean; message: string; data?: { is_active: boolean } }> {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/integrations/wati/triggers/${triggerId}/toggle`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to toggle trigger status'
      };
    }
  }

  // CRM Data for Automation
  async getCrmLeadColumns(): Promise<any[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/integrations/crm/lead-columns`, {
        headers: this.getAuthHeaders()
      });

      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching CRM lead columns:', error);
      return [];
    }
  }

  async getStatusOptions(): Promise<any[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/integrations/crm/status-options`, {
        headers: this.getAuthHeaders()
      });

      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching status options:', error);
      return [];
    }
  }

  // Template parameter mapping (shared endpoint — works for both WATI and AiSensy)
  async saveTemplateParameterMapping(templateId: string, parameterMapping: Record<string, string>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/integrations/templates/${templateId}/parameter-mapping`,
        { parameter_mapping: parameterMapping },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to save parameter mapping'
      };
    }
  }

  // Message Logs
  async getMessageLogs(status?: string, limit: number = 50, offset: number = 0): Promise<{ messages: MessageLog[]; total: number }> {
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await axios.get(
        `${API_BASE_URL}/integrations/wati/logs?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }
      return { messages: [], total: 0 };
    } catch (error) {
      console.error('Error fetching message logs:', error);
      return { messages: [], total: 0 };
    }
  }
}

export default new WatiService();